<template>
  <b-container class="grade-modal-form">

    <b-row align-v="end"
           class="m-0 p-0">

      <b-col class="ml-0 pl-0"
             cols="5"
             lg>

        <label class="mt-2 font-weight-bold"
               for="grade">
          Final grade <br>
          <span class="grey--text">
          (pre-filled with the reviews' average grade - {{ averageGrade }})
          </span>
        </label>
        <b-form-input id="grade"
                      v-model="proposal.final_grade.grade"
                      :readonly="disabled"
                      max="10"
                      min="0"
                      step="0.5"
                      type="number"
                      @paste.prevent/>
      </b-col>

      <b-col class="pt-2 mx-0 px-0"
             cols="2">

        <label class="mt-2 font-weight-bold" for="acceptance">Proposal status</label>
        <div>
          <traffic-lights-buttons id="acceptance"
                                  v-model="proposal.final_grade.acceptance_mark"
                                  :disabled="disabled"
                                  :options="trafficLightsOptions"
                                  stacked/>
        </div>

      </b-col>

      <b-col class="mr-0 pr-0"
             cols="5"
             lg>

        <label class="mt-2 font-weight-bold"
               for="rank">
          Final rank <br>
          <span class="grey--text">
          (pre-filled based on the reviews' average grade - {{ averageRank }})
          </span>
        </label>
        <b-form-select id="rank"
                       v-model="proposal.final_grade.rank"
                       :disabled="disabled"
                       :options="ranks"/>
      </b-col>
    </b-row>

    <b-row class="align-items-end">

      <b-col cols="6">
        <div class="shifts_container">
          <b-form-group :label="shift.text"
                        :label-class="shift.class"
                        class="shifts_item mt-2">
            <span v-if=isNumberOfShiftsOutOfRange
                  class="custom-text-danger">
              The number of shifts must be in range {{ shift.min }} - {{ shift.max }}
            </span>
            <b-form-input v-model="proposal.final_grade[shift.model]"
                          :max="shift.max"
                          :min="shift.min"
                          :readonly="shift.readonly || disabled"
                          :step="shift.step"
                          type="number"
                          @paste.prevent/>
          </b-form-group>
        </div>
      </b-col>

      <b-col cols="6">
        <shifts-counter-reminder v-if="Object.keys(shiftsCounterData).length"
                                 :environment="specifiedEnvironment"
                                 :shifts-counter-data="shiftsCounterData"/>
      </b-col>
    </b-row>

    <b-form-group label="Comment for User (also visible for Solaris management and beamline staff)"
                  label-class="mt-2 font-weight-bold"
                  label-for="comment">
      <b-form-select :disabled="disabled"
                     :options="reviewComments"
                     class="col-md-6"
                     @input="useComment($event)"/>
      <b-form-textarea id="comment"
                       v-model="proposal.final_grade.comments"
                       :readonly="disabled"
                       rows="3"/>
    </b-form-group>

    <b-form-group label="Comment for Solaris management and beamline staff"
                  label-class="mt-2 font-weight-bold"
                  label-for="comments_for_management">
      <b-form-textarea id="comments_for_management"
                       v-model="proposal.final_grade.comments_for_management"
                       :readonly="disabled"
                       rows="3"/>
    </b-form-group>

    <div v-if="Object.keys(reviews).length > 0">
      <b-form-group label="Reviewers comments"
                    label-class="mt-2 font-weight-bold">
        <b-table :current-page="currentPage"
                 :fields="formFields"
                 :items="showReviews(reviews)"
                 :per-page="perPage"
                 bordered
                 class="table-responsive"
                 responsive
                 stacked>
          <template v-slot:row-details="row">
            <b-table :fields="formDetailsFields"
                     :items="[row.item]"
                     class="table-responsive"
                     head-variant="light"
                     responsive/>
          </template>
        </b-table>
      </b-form-group>

      <b-button class="ma-2 right"
                variant="success"
                @click="showDeleted">
        {{ hideDeleted ? 'Show' : 'Hide' }} deleted reviews
      </b-button>

      <b-button v-if="isEditable"
                :disabled="isNumberOfShiftsOutOfRange"
                class="ma-2 right"
                variant="info"
                @click="saveFinalGrade">
        SAVE
      </b-button>

      <b-pagination v-model="currentPage"
                    :per-page="perPage"
                    :total-rows="showReviews(reviews).length"
                    size="md"/>
    </div>

    <p v-else class="mb-0">This proposal has no review yet</p>

  </b-container>
</template>

<script>
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { comments } from '@/json/reviewComments';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMark';
  import TrafficLightsButtons from '@/components/traffic_lights_buttons';
  import ShiftsCounterReminder from '@/components/shifts-counter/ShiftsCounterReminder';
  import _ from 'lodash';

  export default {
    name: 'FinalGradeForm',
    components: {
      ShiftsCounterReminder,
      TrafficLightsButtons,
    },
    mixins: [infrastructureMixin],
    props: {
      isEditable: {
        type: Boolean,
        required: true,
      },
      shiftsCounterData: {
        type: Object,
        default: () => {
        },
      },
      value: {
        type: Object,
        default: () => {
        },
      },
    },
    data() {
      return {
        proposal: JSON.parse(JSON.stringify(this.value)),
        reviews: {},
        hideDeleted: true,
        reviewComments: comments,
        perPage: 5,
        currentPage: 1,
        finalGradeStates: '',
        finalGradeComments: '',
        ranks: ['A*', 'A', 'B', 'C', 'X'],
        specifiedEnvironment: '',
        trafficLightsOptions,
        formFields: [],
        formDetailsFields: [],
        shift: {},
      };
    },
    computed: {
      averageGrade() {
        let temporaryGrade = 0;
        let divisor = 0;
        Object.values(this.reviews).forEach((review) => {
          if (review.grade !== undefined) {
            temporaryGrade += parseFloat(review.grade);
            divisor += 1;
          }
        });
        if (divisor > 0) {
          return (temporaryGrade / divisor).toFixed(2);
        }
        return 'N/A';
      },
      averageRank() {
        const gradeNumber = parseFloat(this.averageGrade);
        if (gradeNumber >= 9) {
          return 'A*';
        }
        if (gradeNumber >= 7 && gradeNumber < 9) {
          return 'A';
        }
        if (gradeNumber >= 4 && gradeNumber < 7) {
          return 'B';
        }
        if (gradeNumber >= 1 && gradeNumber < 4) {
          return 'C';
        }
        if (gradeNumber < 1 && gradeNumber > 0) {
          return 'X';
        }
        return 'N/A';
      },
      disabled() {
        if (!this.isEditable) return true;
        if (this.finalGradeStates !== undefined) {
          if (this.finalGradeStates.name !== undefined) {
            return this.finalGradeStates.name !== 'draft';
          }
          return false;
        }
        return false;
      },
      isNumberOfShiftsOutOfRange() {
        if (this.proposal.final_grade[this.shift.model] === undefined) return false;
        return !_.inRange(this.proposal.final_grade[this.shift.model], this.shift.min, Number(this.shift.max) + 1);
      },
      proposalId() {
        return this.getId(this.proposal);
      },
    },
    watch: {
      averageGrade() {
        if (this.proposal.final_grade.grade === undefined || this.proposal.final_grade.grade === '') {
          this.proposal.final_grade.grade = this.formatAverageGrade(this.averageGrade);
        }
      },
      averageRank() {
        if (this.proposal.final_grade.rank === undefined) {
          this.proposal.final_grade.rank = this.averageRank;
        }
      },
    },
    async created() {
      await this.getFormHelpers();
      this.setDefaultZeroShifts();
      this.loadReviews();
      this.specifiedEnvironment = (
        this.proposal.general.end_station || this.proposal.document_specification.type.toUpperCase()
      );
      this.finalGradeStates = this.proposal.final_grade.states;
      this.finalGradeComments = this.proposal.final_grade.comments;
    },
    methods: {
      formatAverageGrade(average) {
        return (Math.round(average * 100) / 100).toString();
      },
      async getFormHelpers() {
        const helpers = await this.getEvaluationDashboardProperties(this.infrastructure, 'FinalGrade');
        this.formFields = helpers.getSingleField('finalGradeFinalGradesFormFields');
        this.formDetailsFields = helpers.getSingleField('finalGradeFinalGradesFormDetailsFields');
        this.shift = helpers.getSingleField('finalGradeShift');
      },
      loadReviews() {
        this.axios.get(`/documents/${this.proposalId}/review/${this.infrastructure}`)
          .then((response) => {
            this.reviews = response.data;
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Could not load reviews' });
          });
      },
      saveFinalGrade() {
        this.axios.post(
          `/documents/${this.proposalId}/final-grade/${this.infrastructure}`,
          this.proposal.final_grade,
        )
          .then((response) => {
            if (response.data) {
              // eslint-disable-next-line camelcase
              this.proposal.final_grade = response.data;
              this.$emit('input', this.proposal);
              this.$notify({ type: 'success', title: 'Saved successfully!' });
              this.closeModalEmit();
            } else {
              this.$notify({ type: 'error', title: 'Error during saving' });
            }
          })
          .catch(() => {
            this.$notify({ type: 'error', title: 'Error during saving' });
          });
      },
      setDefaultZeroShifts() {
        if (this.proposal.final_grade.number_of_shifts === undefined
          || this.proposal.final_grade.number_of_shifts === '') {
          // eslint-disable-next-line camelcase
          this.proposal.final_grade.number_of_shifts = '0';
        }
      },
      showDeleted() {
        this.hideDeleted = !this.hideDeleted;
      },
      showReviews(reviews) {
        const response = [];
        if (this.hideDeleted) {
          reviews.forEach((review) => {
            if (review.states.name !== 'deleted') {
              response.push({ ...review, _showDetails: true });
            }
          });
        } else {
          reviews.forEach((review) => {
            response.push({ ...review, _showDetails: true });
          });
        }
        return response;
      },
      useComment(option) {
        if (this.finalGradeComments === '' || this.finalGradeComments === undefined) {
          this.$set(this.proposal.final_grade, 'comments', option);
        } else {
          let finalComment = this.finalGradeComments;
          finalComment += `\n${option}`;
          this.finalGradeComments = finalComment;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .pull-right {
    float: right !important;
  }

  .grade-modal-form {
    padding: 0.5rem 0 1rem 0 !important;
    margin: 0.5rem 0 1rem 0 !important;
    max-width: none;
  }

  .shifts_container {
    margin-left: -15px;
    margin-right: -15px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .shifts_item {
    width: 370px;
    margin: 15px
  }
</style>
